<template>
  <div>
    <v-data-table
      ref="dataTable"
      :loading="isLoading || isLoadingTimecards"
      :headers="headers"
      :items="timecards"
      :header-props="{ sortByText: 'ソート' }"
      :footer-props="{ itemsPerPageText: '行/ページ:' }"
      no-data-text="データがありません"
      :page="$route.query.page ? Number($route.query.page) : 1"
      @update:page="($event) => {
        $vuetify.goTo($refs.dataTable)
        addQuery({ page: $event })
      }"
    >
      <template #loading>
        <v-skeleton-loader type="table" />
      </template>

      <template #top>
        <div class="w-full d-flex align-center">
          <v-menu
            v-model="isFilterMenuOpen"
            :close-on-content-click="false"
            min-width="300"
            offset-y
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                class="ml-2"
              >
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ icons.mdiFilterMenuOutline }}
                </v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-subheader>ユーザー</v-subheader>
              <v-list-item>
                <v-list-item-content class="px-2">
                  <user-select
                    :users="users.flatMap(user => user.attributes.role !== 'alliance' ? user : [])"
                    :role-order="['cast', 'waiter']"
                    :prepend-inner-icon="icons.mdiAccountCircleOutline"
                    class="pa-0 ma-0"
                    @selected="getTimecards({ ...$event, ...$route.query })"
                    @cleared="getTimecards($route.query)"
                  />
                </v-list-item-content>
              </v-list-item>

              <v-subheader>ロール</v-subheader>
              <v-list-item>
                <v-list-item-content class="px-2">
                  <simple-select
                    :items="[
                      { id: 'cast', attributes: { name: 'キャスト' } },
                      { id: 'waiter', attributes: { name: 'スタッフ' } },
                    ]"
                    item-id-key-name="role"
                    :prepend-inner-icon="icons.mdiCardAccountDetailsOutline"
                    class="pa-0 ma-0"
                    @selected="getTimecards({ ...$event, ...$route.query })"
                    @cleared="getTimecards($route.query)"
                  />
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-spacer />
          <v-btn
            :loading="isLoading"
            :disabled="timecards.length <= 0 || isLoading"
            :ripple="false"
            color="secondary"
            small
            class="ma-2"
            @click="download"
          >
            <v-icon left>
              {{ icons.mdiFileExcel }}
            </v-icon>
            ダウンロード
          </v-btn>
        </div>
      </template>

      <template #[`item.attributes.startAt`]="{item}">
        <lazy-yona-edit-dialog
          :key="`timecard=${item.id}-startAt-${item.attributes.startAt}`"
          :display-name="item.attributes.startAt ? hhmm(item.attributes.startAt) : '未'"
          :is-valid="!hasErrorInput.timecardStartAt"
          @open="edit({ id: item.id, startAt: item.attributes.startAt ? dateTimeForJsFriendly(item.attributes.startAt) : dateTimeForJsFriendly(new Date()) })"
          @save="update"
        >
          <template #input>
            <v-text-field
              ref="timecardStartAtInput"
              v-model="editTarget.startAt"
              type="datetime-local"
              :max="item.attributes.leaveAt ? dateTimeForJsFriendly(item.attributes.leaveAt) : ''"
              :rules="[
                () => {
                  if (!item.attributes.leaveAt) return true
                  if (editTarget.startAt && item.attributes.leaveAt) {
                    if (dateTimeForJsFriendly(editTarget.startAt.split(' ').join('T')) > dateTimeForJsFriendly(item.attributes.leaveAt.split(' ').join('T'))) return '退勤時刻より前の時刻を入力してください'
                  }

                  return true
                },
              ]"
              class="pa-0 ma-0"
            />
          </template>
        </lazy-yona-edit-dialog>
      </template>

      <template #[`item.attributes.declarations`]="{item}">
        <div
          tabindex="0"
          class="cursor-pointer"
          @click="openDeclarationsDialog(item)"
          @keydown.enter="openDeclarationsDialog(item)"
        >
          <span
            v-for="declaration in item.attributes.declarations"
            :key="`declaration-${item.id}-${declaration.itemId}`"
            class="check-in-tag"
          >
            {{ declarationText(declaration) }}
          </span>
          <span
            v-if="item.attributes.declarations.length === 0"
            class="text--disabled"
          >
            なし
          </span>
          <div
            v-if="item.attributes.punchedAt"
            class="text-xs text--secondary"
          >
            {{ punchText(item) }}
          </div>
        </div>
      </template>

      <template #[`item.attributes.leaveAt`]="{item}">
        <lazy-yona-edit-dialog
          :key="`timecard=${item.id}-leaveAt-${item.attributes.leaveAt}`"
          :display-name="item.attributes.leaveAt ? hhmm(item.attributes.leaveAt) : '未'"
          :is-valid="!hasErrorInput.timecardLeaveAt"
          @open="edit({ id: item.id, leaveAt: item.attributes.leaveAt ? dateTimeForJsFriendly(item.attributes.leaveAt) : dateTimeForJsFriendly(new Date()) })"
          @save="update"
        >
          <template #input>
            <v-text-field
              ref="timecardLeaveAtInput"
              v-model="editTarget.leaveAt"
              type="datetime-local"
              :min="dateTimeForJsFriendly(item.attributes.startAt)"
              :rules="[
                () => {
                  if (editTarget.leaveAt && item.attributes.startAt) {
                    if (dateTimeForJsFriendly(editTarget.leaveAt.split(' ').join('T')) < dateTimeForJsFriendly(item.attributes.startAt.split(' ').join('T'))) return '出勤時刻より後の時刻を入力してください'
                  }

                  return true
                },
              ]"
              class="pa-0 ma-0"
            />
          </template>
        </lazy-yona-edit-dialog>
      </template>

      <template #[`item.attributes.breakCards.data`]="{item}">
        <lazy-yona-edit-dialog
          :key="`timecard=${item.id}-breakTimecards`"
          :is-valid="!hasErrorInput.breakTimecardStartAt && !hasErrorInput.breakTimecardEndAt"
          @save="update"
          @close="cancelEdit"
        >
          <template #append-outer-display-name>
            <v-chip-group
              v-if="item.attributes.breakTimecards.data && item.attributes.breakTimecards.data.length > 0"
              :key="`timecard=${item.id}-breakTimecards-${JSON.stringify(item.attributes.breakTimecards.data)}`"
              column
              small-chips
            >
              <v-chip
                v-for="breakTimecard in item.attributes.breakTimecards.data"
                :key="`breakTimecard${breakTimecard.id}-${breakTimecard.attributes.startAt}-${breakTimecard.attributes.endAt}`"
                small
                @click="edit({
                  id: item.id,
                  breakTimecardsAttributes: [{
                    id: breakTimecard.id,
                    breakTimeStartAt: dateTimeForJsFriendly(breakTimecard.attributes.breakTimeStartAt ? breakTimecard.attributes.breakTimeStartAt : item.attributes.startAt),
                    breakTimeEndAt: dateTimeForJsFriendly(breakTimecard.attributes.breakTimeEndAt ? breakTimecard.attributes.breakTimeEndAt : item.attributes.startAt),
                  }]
                })"
              >
                <span v-if="breakTimecard.attributes.breakTimeStartAt">
                  {{ hhmm(breakTimecard.attributes.breakTimeStartAt) }}
                </span>
                <span class="mx-3">~</span>
                <span v-if="breakTimecard.attributes.breakTimeEndAt">
                  {{ hhmm(breakTimecard.attributes.breakTimeEndAt) }}
                </span>
              </v-chip>
            </v-chip-group>
          </template>

          <template #input>
            <div>
              <span
                v-if="isEditingHere(item.id, 'breakTimecardsAttributes[0]')"
              >
                <v-text-field
                  ref="breakTimecardStartAtInput"
                  v-model="editTarget.breakTimecardsAttributes[0].breakTimeStartAt"
                  type="datetime-local"
                  label="IN"
                  :min="dateTimeForJsFriendly(item.attributes.startAt)"
                  :rules="[
                    () => {
                      if (dateTimeForJsFriendly(editTarget.breakTimecardsAttributes[0].breakTimeStartAt.split(' ').join('T')) < dateTimeForJsFriendly(item.attributes.startAt.split(' ').join('T'))) {
                        return '出勤時刻より後の時刻を入力してください'
                      }

                      return true
                    },
                  ]"
                  class="pa-0 ma-0 my-8"
                />

                <v-text-field
                  ref="breakTimecardEndAtInput"
                  v-model="editTarget.breakTimecardsAttributes[0].breakTimeEndAt"
                  type="datetime-local"
                  label="OUT"
                  :min="dateTimeForJsFriendly(editTarget.breakTimecardsAttributes[0].breakTimeStartAt)"
                  :rules="[
                    () => {
                      if (dateTimeForJsFriendly(editTarget.breakTimecardsAttributes[0].breakTimeEndAt.split(' ').join('T')) < dateTimeForJsFriendly(editTarget.breakTimecardsAttributes[0].breakTimeStartAt.split(' ').join('T'))) {
                        return '休憩入りより後の時刻を入力してください'
                      }

                      return true
                    },
                  ]"
                  class="pa-0 ma-0"
                />
              </span>
            </div>
          </template>
        </lazy-yona-edit-dialog>
      </template>

      <template #[`break_timecard.attributes.breakTimeEndAt`]="{item}">
        <span v-if="item"></span>
      </template>

      <template #[`item.actions`]="{item}">
        <div class="d-flex align-center justify-end flex-wrap">
          <lazy-yona-edit-dialog
            title="削除の確認"
            btn-color="error"
            save-text="同意の上で削除"
            @save="destroy(item.id)"
          >
            <template #append-outer-display-name>
              <v-btn
                icon
                :ripple="false"
              >
                <v-icon small>
                  {{ icons.mdiTrashCanOutline }}
                </v-icon>
              </v-btn>
            </template>

            <template #input>
              <span>
                削除を実行してもよろしいですか（このタイムカードを紐づけたすべての履歴も削除します。）
              </span>
            </template>
          </lazy-yona-edit-dialog>
        </div>
      </template>

      <template #[`footer.page-text`]="props">
        {{ props.pageStart }} - {{ props.pageStop }} / {{ props.itemsLength }}
      </template>
    </v-data-table>

    <check-in-declarations-dialog
      v-model="isDeclarationsDialogOpen"
      :timecard="declarationsTarget"
      :items="declarationItems"
      @save="saveDeclarations"
      @reapply="reapplyCheckInRule"
    />
  </div>
</template>

<script>
import {
  ref,
  computed,
  getCurrentInstance,
  inject,
} from '@vue/composition-api'
import {
  mdiCalendar,
  mdiTrashCanOutline,
  mdiAccountCircleOutline,
  mdiCardAccountDetailsOutline,
  mdiCheckCircleOutline,
  mdiCloseCircleOutline,
  mdiPlus,
  mdiFileExcel,
  mdiFilterMenuOutline,
} from '@mdi/js'
import { cloneDeep } from 'lodash'
import * as XLSX from 'xlsx/xlsx.mjs'
import TimecardApi from '@/api/waiter/Timecard'
import useDateFormat from '@/views/composable/useDateFormat'
import useQueryString from '@/views/composable/useQueryString'
import useDataTableEdit from '@/views/composable/useDataTableEdit'
import useCompInit from '@/views/composable/useCompInit'
import useTimecardsDataTable from '@/views/composable/data-table/useTimecardsDataTable'
import UserSelect from '@/views/components/util/filter/UserSelect.vue'
import SimpleSelect from '@/views/components/util/filter/SimpleSelect.vue'
import LazyYonaEditDialog from '@/views/components/util/LazyYonaEditDialog.vue'
import { TimecardHeaders, TimecardDeclarationsHeader } from '@/utils/constants/table-headers'
import useCheckInSettings from '@/views/composable/useCheckInSettings'
import CheckInDeclarationsDialog from '@/views/components/timecard/CheckInDeclarationsDialog.vue'

export default {
  components: {
    UserSelect,
    SimpleSelect,
    LazyYonaEditDialog,
    CheckInDeclarationsDialog,
  },
  props: {
    date: {
      type: String,
      required: true,
      default: () => new Date().toISOString().substr(0, 10),
    },
    interval: {
      type: Number,
      required: true,
      default: 0,
    },
    endDate: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const isLoadingTimecards = ref(false)
    const timecards = ref([])
    const users = inject('users', [])
    const fetchOperationRateStats = inject('fetchOperationRateStats', () => {})
    const timecardStartAtInput = ref(null)
    const timecardLeaveAtInput = ref(null)
    const breakTimecardStartAtInput = ref(null)
    const breakTimecardEndAtInput = ref(null)
    const isDownloading = ref(false)
    const isFilterMenuOpen = ref(false)

    const { hhmm, dateTimeForJsFriendly } = useDateFormat()
    const { route, addQuery } = useQueryString()
    const {
      isEditing,
      editTarget,
      cancelEdit,
      edit,
      isEditingHere,
    } = useDataTableEdit()
    const { isLoading, initWith } = useCompInit()
    const { buildXlsxData, declarationText } = useTimecardsDataTable(timecards)

    // 出勤時の申告
    const { declarationItems, getCheckInSettings } = useCheckInSettings()
    const isDeclarationsDialogOpen = ref(false)
    const declarationsTarget = ref(null)

    const headers = computed(() => {
      const hasDeclarations = declarationItems.value.length > 0
        || timecards.value.some(timecard => (timecard.attributes.declarations || []).length > 0)
      if (!hasDeclarations) return TimecardHeaders

      return TimecardHeaders.flatMap(header => (header.value === 'actions' ? [TimecardDeclarationsHeader, header] : [header]))
    })

    const punchText = item => `打刻 ${hhmm(item.attributes.punchedAt)}${item.attributes.startAtSource === 'manual' ? '・手修正' : ''}`

    const openDeclarationsDialog = item => {
      declarationsTarget.value = {
        id: item.id,
        userId: item.attributes.userId,
        businessDate: item.meta.date,
        userName: item.meta.userName,
        declarations: item.attributes.declarations,
        punchedAt: item.attributes.punchedAt,
        startAtSource: item.attributes.startAtSource,
      }
      isDeclarationsDialogOpen.value = true
    }

    const hasErrorInput = computed(() => {
      return {
        timecardStartAt: timecardStartAtInput.value?.hasError,
        timecardLeaveAt: timecardLeaveAtInput.value?.hasError,
        breakTimecardStartAt: breakTimecardStartAtInput.value?.hasError,
        breakTimecardEndAt: breakTimecardEndAtInput.value?.hasError,
      }
    })

    const getTimecards = async ({
      date = null,
      interval = null,
      endDate = null,
      userId = null,
      role = ['cast', 'waiter'],
    }) => {
      isLoadingTimecards.value = true

      const res = await TimecardApi.getTimecards({
        date,
        interval,
        endDate,
        userId,
        role,
      })

      if (res?.status === 200) {
        timecards.value = [...res.data.timecards.data]
      }

      isLoadingTimecards.value = false
    }

    const update = async () => {
      // NOTE: 休憩時間の編集はupdateTimecardした後になぜかeditTargetの中身が空になるので
      const editTargetValue = cloneDeep(editTarget.value)

      const res = await TimecardApi.updateTimecard(editTarget.value)

      if (res?.status === 200) {
        const index = timecards.value.findIndex(o => +o.id === +editTargetValue.id)
        timecards.value.splice(index, 1, res.data.timecard.data)
        vm.$toast.success('タイムカードを更新しました')

        fetchOperationRateStats()
        // 締めβで診断(要再集計)を取り直すため、親に変更を伝える
        emit('changed')
      }

      cancelEdit()
    }

    const replaceTimecard = timecard => {
      const index = timecards.value.findIndex(o => +o.id === +timecard.id)
      timecards.value.splice(index, 1, timecard)

      fetchOperationRateStats()
      emit('changed')
    }

    const toastError = (error, fallback) => {
      const messages = error?.response?.data?.message
      vm.$toast.error([].concat(messages || fallback).join(' '))
    }

    // 申告の変更。出勤時刻の再計算はサーバーが行う
    const saveDeclarations = async ({ timecard, declarations }) => {
      try {
        const res = await TimecardApi.updateTimecardWithDeclarations({ id: timecard.id }, declarations)
        replaceTimecard(res.data.timecard.data)
        vm.$toast.success('申告を更新しました')
        isDeclarationsDialogOpen.value = false
      } catch (error) {
        toastError(error, '申告を更新できませんでした')
      }
    }

    const reapplyCheckInRule = async timecard => {
      try {
        const res = await TimecardApi.reapplyCheckInRule(timecard.id)
        replaceTimecard(res.data.timecard.data)
        vm.$toast.success('出勤時刻を自動の時刻に戻しました')
        isDeclarationsDialogOpen.value = false
      } catch (error) {
        toastError(error, '出勤時刻を戻せませんでした')
      }
    }

    const destroy = async id => {
      const res = await TimecardApi.deleteTimecard(id)

      if (res) {
        const index = timecards.value.findIndex(o => +o.id === +id)
        timecards.value.splice(index, 1)
        vm.$toast.success('タイムカードを削除しました')

        fetchOperationRateStats()
        emit('changed')
      }
    }

    const download = () => {
      isDownloading.value = true

      const timecardsExcel = buildXlsxData()

      const wb = XLSX.utils.book_new()
      const data = XLSX.utils.json_to_sheet(timecardsExcel)
      XLSX.utils.book_append_sheet(wb, data, 'タイムカード')

      let title = `${props.date}_${['日', '週', '月'][props.interval]}`
      if (props.endDate) title = `${props.date}~${props.endDate}`
      XLSX.writeFile(wb, `タイムカード_${title}.xlsx`)
      isDownloading.value = false
    }

    initWith([
      getTimecards(route.value.query),
      getCheckInSettings(),
    ])

    return {
      // data
      isLoading,
      isLoadingTimecards,
      isEditing,
      timecards,
      headers,
      declarationItems,
      isDeclarationsDialogOpen,
      declarationsTarget,
      declarationText,
      punchText,
      openDeclarationsDialog,
      saveDeclarations,
      reapplyCheckInRule,
      users,
      editTarget,
      timecardStartAtInput,
      timecardLeaveAtInput,
      breakTimecardStartAtInput,
      breakTimecardEndAtInput,
      isFilterMenuOpen,

      // computed
      hasErrorInput,

      // methods
      hhmm,
      addQuery,
      getTimecards,
      edit,
      cancelEdit,
      update,
      destroy,
      isEditingHere,
      dateTimeForJsFriendly,
      download,

      icons: {
        mdiCalendar,
        mdiTrashCanOutline,
        mdiAccountCircleOutline,
        mdiCardAccountDetailsOutline,
        mdiCheckCircleOutline,
        mdiCloseCircleOutline,
        mdiPlus,
        mdiFileExcel,
        mdiFilterMenuOutline,
      },
    }
  },
}
</script>

<style scoped>
/* Vuetify の color pack は無効のため、色は独自クラスで指定する */
.check-in-tag {
  display: inline-block;
  border-radius: 999px;
  padding: 1px 8px;
  margin: 1px 4px 1px 0;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  background: rgba(145, 85, 253, 0.1);
  color: #6b35d9;
}
</style>
